import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ImagePlus, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BLANK = {
  title: "", address: "", zip: "", price: "", beds: "", baths: "", sqft: "",
  status: "for_sale", category: "Single Family", tag: "", open_house: "", tour_url: "", video_url: "", description: "", images: "", featured: false,
};

const inputCls = "w-full border border-engraved bg-paper px-3 py-2.5 text-sm outline-none focus:border-navy";

const toPayload = (f) => ({
  title: f.title, address: f.address, zip: f.zip,
  price: Number(f.price),
  beds: f.beds === "" || f.beds == null ? null : Number(f.beds),
  baths: f.baths === "" || f.baths == null ? null : Number(f.baths),
  sqft: f.sqft === "" || f.sqft == null ? null : Number(f.sqft),
  status: f.status, category: f.category, tag: f.tag, open_house: f.open_house || "", tour_url: f.tour_url || "", video_url: f.video_url || "", description: f.description,
  images: f.images.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
  featured: f.featured,
});

const ListingForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const imageList = f.images.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setF((prev) => ({ ...prev, images: prev.images ? `${prev.images}\n${url}` : url }));
      }
      toast.success(files.length === 1 ? "Photo uploaded." : `${files.length} photos uploaded.`);
    } catch {
      toast.error("Photo upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) =>
    setF((prev) => ({
      ...prev,
      images: prev.images.split(/[\n,]+/).map((s) => s.trim()).filter((s) => s && s !== url).join("\n"),
    }));

  return (
    <form
      data-testid="admin-listing-form"
      onSubmit={(e) => { e.preventDefault(); onSave(toPayload(f)); }}
      className="mt-6 grid grid-cols-1 gap-4 border border-engraved bg-white p-6 sm:grid-cols-3"
    >
      <input required placeholder="Title" value={f.title} onChange={set("title")} className={inputCls} data-testid="admin-form-title" />
      <input required placeholder="Street Address" value={f.address} onChange={set("address")} className={inputCls} data-testid="admin-form-address" />
      <input placeholder="ZIP" value={f.zip} onChange={set("zip")} className={inputCls} />
      <input required type="number" placeholder="Price" value={f.price} onChange={set("price")} className={inputCls} data-testid="admin-form-price" />
      <input required type="number" step="0.5" placeholder="Beds" value={f.beds} onChange={set("beds")} className={inputCls} />
      <input required type="number" step="0.5" placeholder="Baths" value={f.baths} onChange={set("baths")} className={inputCls} />
      <input required type="number" placeholder="Sq Ft" value={f.sqft} onChange={set("sqft")} className={inputCls} />
      <select value={f.status} onChange={set("status")} className={inputCls} data-testid="admin-form-status">
        <option value="for_sale">For Sale</option>
        <option value="pending">Pending</option>
        <option value="sold">Sold</option>
      </select>
      <input placeholder="Tag (e.g. Price Cut)" value={f.tag} onChange={set("tag")} className={inputCls} />
      <input placeholder="Open House (e.g. Sat Aug 1 · 11 AM – 2 PM)" value={f.open_house || ""} onChange={set("open_house")} className={`${inputCls} sm:col-span-3`} data-testid="admin-form-open-house" />
      <input placeholder="3D Tour / Video URL (optional)" value={f.tour_url || ""} onChange={set("tour_url")} className={`${inputCls} sm:col-span-3`} data-testid="admin-form-tour-url" />
      <input placeholder="Video Walkthrough URL (optional)" value={f.video_url || ""} onChange={set("video_url")} className={`${inputCls} sm:col-span-3`} data-testid="admin-form-video-url" />
      <textarea placeholder="Description" rows={2} value={f.description} onChange={set("description")} className={`${inputCls} sm:col-span-3`} />

      <div className="sm:col-span-3">
        <div className="flex flex-wrap items-center gap-4">
          <label
            data-testid="admin-upload-button"
            className={`flex cursor-pointer items-center gap-2 border border-navy px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-navy transition-colors hover:bg-navy hover:text-paper ${uploading ? "pointer-events-none opacity-60" : ""}`}
          >
            <ImagePlus size={14} />
            {uploading ? "Uploading…" : "Upload Photos"}
            <input
              data-testid="admin-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              onChange={uploadFiles}
            />
          </label>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
            JPG · PNG · WEBP — up to 10 MB each
          </span>
        </div>
        {imageList.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-3">
            {imageList.map((url) => (
              <div key={url} className="group relative border border-engraved">
                <img src={url} alt="" className="h-16 w-24 object-cover" />
                <button
                  type="button"
                  data-testid="admin-remove-image-button"
                  onClick={() => removeImage(url)}
                  className="absolute -right-2 -top-2 bg-crimson p-0.5 text-paper opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Remove photo"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          placeholder="Image URLs (one per line) — or use Upload Photos above"
          rows={2}
          value={f.images}
          onChange={set("images")}
          className={`${inputCls} mt-4`}
          data-testid="admin-form-images"
        />
      </div>

      <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-600">
        <input type="checkbox" checked={f.featured} onChange={set("featured")} /> Featured
      </label>
      <div className="flex gap-3 sm:col-span-2 sm:justify-end">
        <button type="button" onClick={onCancel} className="border border-engraved px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-600 hover:border-navy">
          Cancel
        </button>
        <button data-testid="admin-listing-save-button" className="bg-navy px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-paper hover:bg-crimson">
          Save Listing
        </button>
      </div>
    </form>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/listings").then(({ data }) => setListings(data)).catch(() => {});
    api.get("/admin/appointments").then(({ data }) => setAppointments(data)).catch(() => {});
  }, [user]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-paper font-mono text-xs text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const saveListing = async (payload) => {
    try {
      if (editing?.id) {
        const { data } = await api.put(`/admin/listings/${editing.id}`, payload);
        setListings((ls) => ls.map((l) => (l.id === data.id ? data : l)));
        toast.success("Listing updated.");
      } else {
        const { data } = await api.post("/admin/listings", payload);
        setListings((ls) => [data, ...ls]);
        toast.success("Listing added.");
      }
      setEditing(null);
    } catch {
      toast.error("Could not save listing.");
    }
  };

  const deleteListing = async (id) => {
    if (!window.confirm("Remove this listing?")) return;
    try {
      await api.delete(`/admin/listings/${id}`);
      setListings((ls) => ls.filter((l) => l.id !== id));
      toast.success("Listing removed.");
    } catch {
      toast.error("Could not delete listing.");
    }
  };

  const setAppointmentStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      setAppointments((as) => as.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch {
      toast.error("Could not update appointment.");
    }
  };

  const newCount = appointments.filter((a) => a.status === "new").length;

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="bg-navy text-paper">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
          <div>
            <p className="font-cinzel text-sm font-bold tracking-[0.2em]">AGENT PORTAL</p>
            <p className="font-mono text-[10px] text-paper/60">{user.email}</p>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors hover:border-gold hover:text-gold"
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <div className="flex gap-2 border-b border-engraved pb-4">
          <button
            data-testid="admin-tab-listings"
            onClick={() => setTab("listings")}
            className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] ${tab === "listings" ? "bg-navy text-paper" : "text-slate-600 hover:text-crimson"}`}
          >
            Listings ({listings.length})
          </button>
          <button
            data-testid="admin-tab-appointments"
            onClick={() => setTab("appointments")}
            className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] ${tab === "appointments" ? "bg-navy text-paper" : "text-slate-600 hover:text-crimson"}`}
          >
            Appointments {newCount > 0 && <span className="ml-1 bg-crimson px-1.5 py-0.5 text-paper">{newCount}</span>}
          </button>
        </div>

        {tab === "listings" && (
          <div className="mt-8">
            {!editing && (
              <button
                data-testid="admin-add-property-button"
                onClick={() => setEditing({ ...BLANK })}
                className="flex items-center gap-2 bg-crimson px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-paper transition-colors hover:bg-navy"
              >
                <Plus size={14} /> Add Property
              </button>
            )}
            {editing && (
              <ListingForm
                initial={editing.id ? { ...editing, images: (editing.images || []).join("\n"), beds: editing.beds ?? "", baths: editing.baths ?? "", sqft: editing.sqft ?? "" } : editing}
                onSave={saveListing}
                onCancel={() => setEditing(null)}
              />
            )}
            <div className="mt-8 space-y-3">
              {listings.map((l) => (
                <div key={l.id} data-testid={`admin-listing-row-${l.id}`} className="flex flex-wrap items-center gap-4 border border-engraved bg-white p-4">
                  <img src={l.images?.[0]} alt="" className="h-14 w-20 shrink-0 border border-engraved object-cover" />
                  <div className="min-w-[180px] flex-1">
                    <p className="font-serif text-base font-bold text-navy">{l.title}</p>
                    <p className="text-xs text-slate-500">{l.address} · {l.zip}</p>
                  </div>
                  <p className="font-mono text-sm text-ink">{fmtPrice(l.price)}</p>
                  <p className="font-mono text-[11px] text-slate-500">{l.beds}BD/{l.baths}BA</p>
                  <span className={`px-2.5 py-1 font-cinzel text-[9px] font-bold uppercase tracking-[0.15em] ${l.status === "for_sale" ? "bg-crimson text-paper" : l.status === "pending" ? "bg-gold text-navy" : "bg-navy text-paper"}`}>
                    {l.status.replace("_", " ")}
                  </span>
                  <div className="flex gap-2">
                    <button
                      data-testid={`admin-edit-listing-${l.id}`}
                      onClick={() => setEditing(l)}
                      className="border border-engraved p-2 text-slate-600 transition-colors hover:border-navy hover:text-navy"
                      aria-label="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`admin-delete-listing-${l.id}`}
                      onClick={() => deleteListing(l.id)}
                      className="border border-engraved p-2 text-slate-600 transition-colors hover:border-crimson hover:text-crimson"
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "appointments" && (
          <div className="mt-8 space-y-4">
            {appointments.length === 0 && (
              <p data-testid="appointments-empty" className="border border-engraved bg-white p-12 text-center text-sm text-slate-500">
                No appointment requests yet.
              </p>
            )}
            {appointments.map((a) => (
              <div key={a.id} data-testid={`appointment-card-${a.id}`} className="flex flex-wrap items-center gap-x-6 gap-y-3 border border-engraved bg-white p-5">
                <div className="min-w-[160px] flex-1">
                  <p className="font-serif text-lg font-bold text-navy">{a.name}</p>
                  <p className="font-mono text-xs text-slate-600">{a.phone}{a.email ? ` · ${a.email}` : ""}</p>
                </div>
                <p className="font-mono text-xs text-ink">{a.date} · {a.time}</p>
                <span className="bg-paper-cream px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-navy">
                  {a.service_type.replace("_", " ")}
                </span>
                {a.listing_label && <p className="w-full text-xs text-slate-500">Re: {a.listing_label}</p>}
                {a.notes && <p className="w-full text-xs italic text-slate-500">“{a.notes}”</p>}
                <select
                  data-testid={`appointment-status-${a.id}`}
                  value={a.status}
                  onChange={(e) => setAppointmentStatus(a.id, e.target.value)}
                  className="ml-auto border border-engraved bg-paper px-3 py-2 text-xs outline-none focus:border-navy"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
